import type { DateKey, HHMM } from './time';
import { WEEKDAYS, addDays, weekdayOf } from './time';

/** The fields of the server's `Alarm` that scheduling needs. `days`: 0=Sunday…6; empty = once. */
export interface AlarmLike {
  time: HHMM;
  days: number[];
  enabled: boolean;
}

const toMinutes = (hhmm: HHMM) => Number(hhmm.slice(0, 2)) * 60 + Number(hhmm.slice(3, 5));

/** Whether the alarm rings at this household-local minute. */
export function alarmRingsAt(a: AlarmLike, dateKey: DateKey, hhmm: HHMM): boolean {
  if (!a.enabled || a.time !== hhmm) return false;
  return a.days.length === 0 || a.days.indexOf(weekdayOf(dateKey)) >= 0;
}

/** The alarm's next ring strictly after `hhmm` on `dateKey`; null when it is switched off. */
export function nextAlarm(a: AlarmLike, dateKey: DateKey, hhmm: HHMM): { dateKey: DateKey; minutesUntil: number } | null {
  if (!a.enabled) return null;
  const now = toMinutes(hhmm);
  const at = toMinutes(a.time);
  for (let i = 0; i <= 7; i++) {
    if (i === 0 && at <= now) continue;
    const d = addDays(dateKey, i);
    if (a.days.length === 0 || a.days.indexOf(weekdayOf(d)) >= 0) return { dateKey: d, minutesUntil: i * 1440 + at - now };
  }
  return null;
}

/** The enabled alarm that rings soonest, if any. */
export function soonestAlarm<T extends AlarmLike>(alarms: T[], dateKey: DateKey, hhmm: HHMM): { alarm: T; minutesUntil: number } | null {
  let best: { alarm: T; minutesUntil: number } | null = null;
  for (const a of alarms) {
    const n = nextAlarm(a, dateKey, hhmm);
    if (n && (!best || n.minutesUntil < best.minutesUntil)) best = { alarm: a, minutesUntil: n.minutesUntil };
  }
  return best;
}

/** "Once", "Every day", "Weekdays", "Weekends" or "Mon, Wed, Fri". */
export function repeatLabel(days: number[]): string {
  const s = days.slice().sort((a, b) => a - b);
  const key = s.join(',');
  if (s.length === 0) return 'Once';
  if (s.length === 7) return 'Every day';
  if (key === '1,2,3,4,5') return 'Weekdays';
  if (key === '0,6') return 'Weekends';
  return s.map((d) => WEEKDAYS[d]!.slice(0, 3)).join(', ');
}

/** "in 45 min", "in 8 h 20 min", "in 2 days" for an alarm's next ring. */
export function untilLabel(minutes: number): string {
  if (minutes < 60) return `in ${minutes} min`;
  if (minutes >= 48 * 60) return `in ${Math.floor(minutes / 1440)} days`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m ? `in ${h} h ${m} min` : `in ${h} h`;
}

const pad = (n: number) => (n < 10 ? `0${n}` : String(n));

/** Stopwatch reading: "MM:SS.cc", or "H:MM:SS.cc" from an hour. */
export function formatStopwatch(ms: number): string {
  const t = Math.max(0, Math.floor(ms / 10));
  const cs = t % 100;
  const s = Math.floor(t / 100) % 60;
  const m = Math.floor(t / 6000) % 60;
  const h = Math.floor(t / 360000);
  return h ? `${h}:${pad(m)}:${pad(s)}.${pad(cs)}` : `${pad(m)}:${pad(s)}.${pad(cs)}`;
}

/** Timer reading, rounded up to the second so it shows 0:00 only when done: "M:SS" / "H:MM:SS". */
export function formatTimer(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const s = total % 60;
  const m = Math.floor(total / 60) % 60;
  const h = Math.floor(total / 3600);
  return h ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
}

/**
 * Stopwatch and timer are wall-clock based (start timestamps, not tick counters) so they stay
 * right across re-renders, sleeps and reloads.
 */
export interface StopwatchState {
  startedAt: number | null;
  /** Time accumulated before the current run. */
  banked: number;
  laps: number[];
}

export const STOPWATCH_IDLE: StopwatchState = { startedAt: null, banked: 0, laps: [] };

export function stopwatchElapsed(s: StopwatchState, now: number): number {
  return s.banked + (s.startedAt === null ? 0 : Math.max(0, now - s.startedAt));
}

export function stopwatchToggle(s: StopwatchState, now: number): StopwatchState {
  return s.startedAt === null ? { ...s, startedAt: now } : { ...s, startedAt: null, banked: stopwatchElapsed(s, now) };
}

/** Records a lap as the elapsed total at that moment; newest first. */
export function stopwatchLap(s: StopwatchState, now: number): StopwatchState {
  return s.startedAt === null ? s : { ...s, laps: [stopwatchElapsed(s, now), ...s.laps] };
}

export interface TimerState {
  durationMs: number;
  /** Set while running. */
  endsAt: number | null;
  /** Set while paused. */
  pausedLeft: number | null;
}

export const TIMER_PRESETS_MIN = [1, 3, 5, 10, 15, 30, 60];

export function timerIdle(durationMs: number): TimerState {
  return { durationMs, endsAt: null, pausedLeft: null };
}

export function timerLeft(t: TimerState, now: number): number {
  if (t.endsAt !== null) return Math.max(0, t.endsAt - now);
  return t.pausedLeft !== null ? t.pausedLeft : t.durationMs;
}

export function timerStart(t: TimerState, now: number): TimerState {
  if (t.endsAt !== null) return t;
  return { ...t, endsAt: now + timerLeft(t, now), pausedLeft: null };
}

export function timerPause(t: TimerState, now: number): TimerState {
  if (t.endsAt === null) return t;
  return { ...t, endsAt: null, pausedLeft: timerLeft(t, now) };
}

/** Adds time to a running, paused or idle timer (the "+1 min" button). */
export function timerAdd(t: TimerState, ms: number): TimerState {
  if (t.endsAt !== null) return { ...t, endsAt: t.endsAt + ms };
  if (t.pausedLeft !== null) return { ...t, pausedLeft: t.pausedLeft + ms };
  return { ...t, durationMs: t.durationMs + ms };
}
